// src/services/outbox.js — durable queue for writes made without signal.
//
// Crews work dead zones all over NW Georgia. When a write fails because the
// network is gone, it lands here instead of being lost, and replays in order when
// signal comes back.
//
// Rules that make replay safe:
//
//  * ONLY append-only, crew-scoped operations are queueable (see QUEUEABLE in
//    offlineSync.js). Anything last-write-wins — a job status change, a job edit —
//    stays online-only, because replaying it an hour later would silently clobber
//    whatever the office changed in the meantime.
//  * Every op carries `client_op_id` so the server applies it exactly once even if
//    a drain half-fails and retries.
//  * Every op carries `occurred_at` — the moment the crew actually did it. A
//    4:30pm clock-out that syncs at 6:15pm must still record 4:30pm or payroll is
//    quietly wrong.
//  * Drain is strictly FIFO and sequential. A clock-out must never be replayed
//    before the clock-in it belongs to.

import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken } from './authToken';
import {
  STORE_OUTBOX, STORE_IDMAP,
  dbAdd, dbGetAll, dbPut, dbDelete, dbGet, storageEstimate,
} from './offlineDb';

// Bare client with no offline interceptor — the drain must never re-queue itself.
const raw = axios.create({ baseURL: API_URL, withCredentials: true });

// Give a weak connection room to actually finish an upload before we call it dead.
const DRAIN_TIMEOUT_MS = 45000;
// Past this many failed attempts an op is parked as 'failed' and shown to the user
// rather than retried forever.
const MAX_ATTEMPTS = 6;
// Stop accepting photos once storage is this full, so the queue itself can't be
// evicted by the browser to make room for more photos.
const STORAGE_CEILING = 0.85;

export const OP_PENDING = 'pending';
export const OP_FAILED = 'failed';

// ---------------------------------------------------------------- subscribers

const listeners = new Set();

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function notify() {
  const ops = await getAll();
  listeners.forEach((fn) => {
    try {
      fn(ops);
    } catch (e) {
      /* a bad listener must not break the drain */
    }
  });
}

// ------------------------------------------------------------------- helpers

export function newOpId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Older WebViews: good enough for an idempotency key scoped to one device.
  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// A queued op that creates a record other queued ops depend on (clock-in ->
// clock-out) publishes its future server id under this placeholder.
export function placeholderFor(opId) {
  return `local-${opId}`;
}

export function isPlaceholder(value) {
  return typeof value === 'string' && value.startsWith('local-');
}

async function resolvePlaceholders(url) {
  if (!url.includes('local-')) return url;
  const match = url.match(/local-[\w-]+/);
  if (!match) return url;
  const mapped = await dbGet(STORE_IDMAP, match[0]);
  if (!mapped || mapped.realId == null) return null; // parent hasn't landed yet
  return url.replace(match[0], String(mapped.realId));
}

// --------------------------------------------------------------- queue access

export async function getAll() {
  const ops = await dbGetAll(STORE_OUTBOX);
  return ops.sort((a, b) => a.id - b.id);
}

export async function getPending() {
  return (await getAll()).filter((op) => op.status !== OP_FAILED);
}

export async function getFailed() {
  return (await getAll()).filter((op) => op.status === OP_FAILED);
}

export async function countPending() {
  return (await getPending()).length;
}

/**
 * Queue a write for replay. Returns the stored op.
 *
 * @param {object} op
 * @param {string} op.method    'post' | 'put'
 * @param {string} op.url       path relative to API_URL, e.g. '/time-entries/clock-in'
 * @param {string} op.kind      machine label, drives the optimistic UI overlay
 * @param {string} op.label     human text shown in the pending list
 * @param {object} [op.data]    JSON body
 * @param {Array}  [op.form]    [[key, value], ...] for multipart
 * @param {Blob}   [op.blob]    file payload (photos)
 * @param {string} [op.opId]    supply to keep a caller-side reference
 */
export async function enqueue(op) {
  const opId = op.opId || newOpId();
  const record = {
    opId,
    method: (op.method || 'post').toLowerCase(),
    url: op.url,
    kind: op.kind || 'write',
    label: op.label || 'Pending change',
    jobId: op.jobId ?? null,
    data: op.data ?? null,
    form: op.form ?? null,
    blob: op.blob ?? null,
    blobName: op.blobName ?? null,
    blobType: op.blobType ?? null,
    // The moment the crew did the thing. The server trusts this over its own clock.
    occurredAt: op.occurredAt || new Date().toISOString(),
    // Placeholder this op will publish once the server assigns it a real id.
    publishesPlaceholder: op.publishesPlaceholder || null,
    createdAt: Date.now(),
    attempts: 0,
    lastError: null,
    status: OP_PENDING,
  };
  record.id = await dbAdd(STORE_OUTBOX, record);
  await notify();
  return record;
}

export async function removeOp(id) {
  await dbDelete(STORE_OUTBOX, id);
  await notify();
}

/** Drop a permanently-failed op the user has chosen to discard. */
export async function discard(id) {
  await removeOp(id);
}

/** Put a failed op back in line — used by the "Try again" button. */
export async function retryOp(id) {
  const op = await dbGet(STORE_OUTBOX, id);
  if (!op) return;
  op.status = OP_PENDING;
  op.attempts = 0;
  op.lastError = null;
  await dbPut(STORE_OUTBOX, op);
  await notify();
  drain();
}

/**
 * Photos for a job still sitting in the queue, shaped like the API's photo rows so
 * a gallery can render them alongside synced ones.
 *
 * Without this a crew shoots a corner offline, the upload is queued, the gallery
 * refetches from cache and the photo simply isn't there — which reads as "it
 * didn't save" and gets it shot again.
 *
 * The returned `url` is an object URL owned by the caller; revoke it on unmount.
 */
export async function getQueuedPhotos(jobId) {
  const ops = await getPending();
  return ops
    .filter((op) => op.kind === 'photo' && (jobId == null || op.jobId === Number(jobId)))
    .map((op) => ({
      id: `queued-${op.opId}`,
      job_id: op.jobId,
      url: op.blob ? URL.createObjectURL(op.blob) : null,
      original_filename: op.blobName || 'photo.jpg',
      caption: (op.form || []).find(([k]) => k === 'caption')?.[1] || '',
      created_at: op.occurredAt,
      file_size: op.blob ? op.blob.size : 0,
      __queued: true,
    }));
}

/** True when there's no room left to safely stash another photo. */
export async function storageIsTight() {
  const est = await storageEstimate();
  if (!est) return false; // unknown — let it through rather than block the crew
  return est.pctUsed >= STORAGE_CEILING;
}

// -------------------------------------------------------------------- draining

let draining = false;
let drainQueued = false;
// Set when the server rejects our token. Backend restarts wipe in-memory tokens
// and log everyone out, so this is a normal event, not an error — we hold the
// queue intact and wait for the user to sign back in.
let pausedForAuth = false;

export function isPausedForAuth() {
  return pausedForAuth;
}

export function resumeAfterAuth() {
  pausedForAuth = false;
  drain();
}

function isNetworkError(err) {
  return !err.response && (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !!err.request);
}

// 4xx means the server understood and refused — replaying won't help. 408/429 are
// the exceptions: those are "try later", not "never".
function isPermanent(status) {
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

async function buildRequest(op, token) {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  // Sent as headers too so multipart uploads don't have to thread them through
  // the form body.
  headers['X-Client-Op-Id'] = op.opId;
  headers['X-Occurred-At'] = op.occurredAt;

  if (op.blob) {
    const form = new FormData();
    (op.form || []).forEach(([k, v]) => form.append(k, v));
    form.append('client_op_id', op.opId);
    form.append('occurred_at', op.occurredAt);
    form.append('photo', op.blob, op.blobName || 'photo.jpg');
    return { headers, data: form };
  }

  return {
    headers,
    data: { ...(op.data || {}), client_op_id: op.opId, occurred_at: op.occurredAt },
  };
}

/**
 * Replay the queue oldest-first. Safe to call repeatedly — concurrent calls
 * collapse into one pass, and a pass that starts mid-drain runs again after.
 */
export async function drain() {
  if (draining) {
    drainQueued = true;
    return;
  }
  if (pausedForAuth) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

  draining = true;
  try {
    const token = await getAuthToken();
    if (!token) {
      // Logged out — hold everything. The queue survives; it drains after login.
      pausedForAuth = true;
      return;
    }

    const ops = await getPending();
    for (const op of ops) {
      // Depends on an op that hasn't landed yet — stop here rather than skip, so
      // strict ordering holds.
      const url = await resolvePlaceholders(op.url);
      if (url === null) break;

      try {
        const { headers, data } = await buildRequest(op, token);
        const res = await raw.request({
          method: op.method,
          url,
          data,
          headers,
          timeout: DRAIN_TIMEOUT_MS,
          __isDrain: true, // belt-and-braces: never let the drain re-queue itself
        });

        // Publish the real id so dependent ops can resolve their placeholder.
        if (op.publishesPlaceholder) {
          const realId = res?.data?.id ?? res?.data?.entries?.[0]?.id ?? null;
          if (realId != null) {
            await dbPut(STORE_IDMAP, { placeholder: op.publishesPlaceholder, realId });
          }
        }

        await dbDelete(STORE_OUTBOX, op.id);
        await notify();
      } catch (err) {
        if (err.response && err.response.status === 401) {
          // Token died (very often: the backend was restarted). Keep the queue.
          pausedForAuth = true;
          await notify();
          return;
        }

        if (isNetworkError(err)) {
          // Signal dropped again mid-drain. Leave the rest queued, try later.
          return;
        }

        const status = err.response ? err.response.status : 0;
        op.attempts = (op.attempts || 0) + 1;
        op.lastError = err.response?.data?.error || err.message || 'Sync failed';

        if (isPermanent(status) || op.attempts >= MAX_ATTEMPTS) {
          // Park it and surface it — never silently drop a crew's work.
          op.status = OP_FAILED;
          await dbPut(STORE_OUTBOX, op);
          await notify();
          continue; // a poison op must not block everything behind it
        }

        await dbPut(STORE_OUTBOX, op);
        await notify();
        return; // transient server trouble — back off, retry on the next trigger
      }
    }
  } finally {
    draining = false;
    if (drainQueued) {
      drainQueued = false;
      setTimeout(drain, 0);
    }
  }
}

// ------------------------------------------------------------------- triggers

let started = false;

/** Wire up the events that should kick a drain. Called once from api.js. */
export function startOutbox() {
  if (started || typeof window === 'undefined') return;
  started = true;

  window.addEventListener('online', () => drain());
  // Coming back to the app after a drive is the most common moment signal
  // returns without an 'online' event firing.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') drain();
  });
  // Backstop for flaky mobile signal, where 'online' lies.
  setInterval(() => drain(), 60000);

  drain();
}
